import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {
    signupFormModalOpened,
    SignupFormModalOpenedAction,
    signupFormSubmitted,
} from '@/analytics/segment'
import Button from '@/components/button'
import {GlobalText} from '@/components/global-text'
import Input from '@/components/input'
import {Modal, ModalContent, ModalActions} from '@/components/modal'
import type {Props as ModalProps} from '@/components/modal'
import {text} from '@/components/text'
import type {DefaultSignupModalQuery} from '@/graphql-types'
import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import useSignup from '@/hooks/use-signup'
import type {Args} from '@/hooks/use-signup'
import checkIconPink from '@/images/icons/check-pink.svg'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {CheckmarkList} from '@/styles/lists'
import {tokens, colors} from '@/theme'
import type {FormConfig} from '@/types/consents'
import getEnvironmentField from '@/utils/get-environment-field'
import {isPlaywright} from '@/utils/playwright'

import {UnifiedSignupModal} from '../unified-signup-modal/unified-signup-modal'

export interface Props extends Omit<ModalProps, 'children'> {
    signupProps?: Args
    formConfig?: FormConfig
    highlightedFeatures?: string[]
    onSignup?: (value: boolean) => void
}

const getContent = (
    overrideDefaultContent: Partial<Props>,
    defaultContent: DefaultSignupModalQuery['allPrismicSignupmodal']['nodes'][0]['data'],
) => ({
    ...defaultContent.signup_modal[0],
    title: overrideDefaultContent.title || defaultContent.signup_modal[0].title,
    subtitle: overrideDefaultContent.subtitle || defaultContent.signup_modal[0].subtitle,
    image: overrideDefaultContent.image || {
        url: defaultContent.signup_modal[0].image.fluid.base64,
        dimensions: {
            width: 80,
            height: 80,
        },
        copyright: null,
        alt: null,
    },
    formConfig:
        overrideDefaultContent.formConfig ||
        getEnvironmentField({
            production: defaultContent.form_production,
            staging: defaultContent.form_staging,
        }),
    highlightedFeatures:
        overrideDefaultContent.highlightedFeatures ||
        [
            defaultContent.signup_modal[0].highlighted_feature_1,
            defaultContent.signup_modal[0].highlighted_feature_2,
            defaultContent.signup_modal[0].highlighted_feature_3,
        ].filter((item) => Boolean(item)),
})

export const SignupModal = ({
    isOpen,
    onDismiss,
    onSignup,
    title,
    subtitle,
    image,
    formConfig,
    signupProps = {form: 'Front'},
    highlightedFeatures,
}: Partial<Props>) => {
    const currentLocale = useLocale()
    const {allPrismicSignupmodal} = useStaticQuery<DefaultSignupModalQuery>(query)
    const [defaultContent] = useLocalizedContent(allPrismicSignupmodal.nodes)
    const overrideContent = {
        title,
        subtitle,
        image,
        formConfig,
        highlightedFeatures,
    }

    const defaultModalContent = defaultContent.data.signup_modal?.[0] || {}

    const defaultContentTransformed = {
        ...defaultContent.data,
        signup_modal: [
            {
                ...defaultModalContent,
                title: signupProps.isPartner
                    ? defaultModalContent.partner_title
                    : defaultModalContent.title,
                subtitle: signupProps.isPartner
                    ? defaultModalContent.partner_subtitle
                    : defaultModalContent.subtitle,
            },
        ],
    }
    const content = getContent(overrideContent, defaultContentTransformed)

    const globalStrings = useGlobalStrings()
    const {handleSubmit, clearError, setEmail, email, loading, error, handleEmailChange} =
        useSignup(signupProps, content.formConfig.formId)

    const errorMessage = useEmailValidationErrorMessage(error)

    const handleDismiss = () => {
        clearError()
        setEmail('')
        onDismiss?.()
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        signupFormSubmitted({marketingConsent: false, privacyConsent: false})
        handleSubmit(event)
    }

    const hasHighlightedFeatures =
        content.highlightedFeatures && content.highlightedFeatures.length > 0

    React.useEffect(() => {
        if (isOpen) {
            signupFormModalOpened({action: SignupFormModalOpenedAction.Opened})
        }
    }, [isOpen])

    React.useEffect(() => {
        if (onSignup) {
            onSignup(loading)
        }
    }, [loading, onSignup])

    return currentLocale.code === 'en-gb' && !isPlaywright() ? (
        <UnifiedSignupModal isOpen={isOpen} onDismiss={handleDismiss} signupProps={signupProps} />
    ) : (
        <Modal
            title={content.title}
            subtitle={content.subtitle}
            image={content.image}
            isOpen={isOpen}
            onDismiss={handleDismiss}
        >
            <form onSubmit={onSubmit} noValidate>
                <ModalContent>
                    {hasHighlightedFeatures && (
                        <FeaturesList>
                            {content.highlightedFeatures.map((feature, idx) => (
                                <Item key={idx}>{feature}</Item>
                            ))}
                        </FeaturesList>
                    )}

                    <Input
                        type="email"
                        label={globalStrings.work_email}
                        value={email}
                        onChange={(value) => {
                            handleEmailChange({
                                target: {value},
                            } as React.ChangeEvent<HTMLInputElement>)
                        }}
                        error={errorMessage}
                        shouldAutoFocus={isOpen}
                        data-testid="modal-input"
                    />
                </ModalContent>

                <ModalActions>
                    <Button type="submit" loading={loading} data-testid="modal-submit-button">
                        {globalStrings.submit}
                    </Button>
                </ModalActions>
                <StyledGlobalText globalStringKey="financial_partner_disclaimer" />
            </form>
        </Modal>
    )
}

const FeaturesList = styled(CheckmarkList)`
    margin: 0;
    gap: ${tokens.spacing4};
`

const Item = styled.li`
    ${text({$variant: 'paragraph-base'})}
    color: ${colors.text.muted};

    &::before {
        padding-right: ${tokens.spacing12};
        content: url(${checkIconPink});
    }
`

const StyledGlobalText = styled(GlobalText)`
    ${text({$variant: 'label-base'})};
    margin-top: ${tokens.spacing12};
`

const query = graphql`
    query DefaultSignupModal {
        allPrismicSignupmodal {
            nodes {
                lang
                data {
                    form_production {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    form_staging {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    signup_modal {
                        image {
                            fluid {
                                base64
                            }
                        }
                        highlighted_feature_1
                        highlighted_feature_2
                        highlighted_feature_3
                        title
                        subtitle
                        partner_title
                        partner_subtitle
                    }
                }
            }
        }
    }
`
